import * as React from 'react';
import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { translate } from '~l10n/client';
import cx from 'classnames';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import { UIButton } from '~shared/components/buttons';
import { Dialog, DialogProps } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import { useCopyWorkspaceQuiz } from '~teacher/hook/workspaces/useCopyWorkspaceQuiz';
import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

interface DuplicateWorkspaceQuizDialogProps
  extends Pick<DialogProps, 'open'>,
    DuplicateWorkspaceQuizDialogContentProps {}

export function DuplicateWorkspaceQuizDialog({
  open,
  onClose,
  initialQuizName,
  workspaceQuizId,
  workspaceId,
}: DuplicateWorkspaceQuizDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      closeLabel={translate('Close')}
      id="duplicate-workspace-quiz"
      title={translate('Duplicate Quiz')}
    >
      <DuplicateWorkspaceQuizDialogContent
        key={`${workspaceId}-${workspaceQuizId}-${initialQuizName}`}
        workspaceId={workspaceId}
        workspaceQuizId={workspaceQuizId}
        initialQuizName={initialQuizName}
        onClose={onClose}
      />
    </Dialog>
  );
}

interface DuplicateWorkspaceQuizDialogContentProps
  extends Pick<DialogProps, 'onClose'> {
  initialQuizName: string;
  workspaceQuizId: string;
  workspaceId: string;
}

function DuplicateWorkspaceQuizDialogContent({
  workspaceQuizId,
  initialQuizName,
  onClose,
  workspaceId,
}: DuplicateWorkspaceQuizDialogContentProps) {
  const [quizName, setQuizName] = useState(initialQuizName);
  const [isSavingWorkspaceQuiz, setIsSavingWorkspaceQuiz] = useState(false);
  const copyWorkspaceQuiz = useCopyWorkspaceQuiz();

  const autoFocusOnOpenCallback = useAutoFocus();
  const queryClient = useQueryClient();

  const newNameInputClass = cx('w-26 mt-3', {
    'border-red-800': copyWorkspaceQuiz.isError,
  });

  return (
    <>
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <div className="text-base leading-8">
        <label htmlFor="new-quiz-name-input">{translate('Quiz Title')}</label>
        <TextInput
          ref={autoFocusOnOpenCallback}
          value={quizName}
          id="new-quiz-name-input"
          withBorder
          className={newNameInputClass}
          onChange={(event) => setQuizName(event.target.value)}
        />
      </div>
      <div className="pt-8">
        <Inline align="right">
          <UIButton
            isDisabled={isSavingWorkspaceQuiz}
            onClick={() => onClose(false)}
          >
            {translate('Cancel')}
          </UIButton>
          <UIButton
            onClick={() => {
              setIsSavingWorkspaceQuiz(true);
              copyWorkspaceQuiz.mutate(
                {
                  workspaceId: workspaceId,
                  workspaceQuizId: workspaceQuizId,
                  newName: quizName,
                },
                {
                  onError: () => {
                    toast.critical(translate('An error has occurred'));
                  },
                  onSuccess: async () => {
                    toast.positive(translate('Copied'));
                    await queryClient.invalidateQueries(workspaceKeys.list());
                    new CreateQuizEventAdapter({
                      creation_method: 'copy',
                      source_workspace_id: workspaceId,
                      destination_workspace_id: workspaceId,
                    }).sendAnalytics();
                    onClose(false);
                  },
                }
              );
            }}
            isDisabled={isSavingWorkspaceQuiz}
            appearance="primary"
          >
            {translate('Duplicate')}
          </UIButton>
        </Inline>
      </div>
    </>
  );
}
